/** Form for one gauge. Every change updates the live preview immediately (draft), Save persists it. */
import { useTranslation } from 'react-i18next';
import type { Gauge, NeedleSettings, AssignmentSettings, LightSettings } from '@dg/core';
import { formatValue } from './ClusterPreview.tsx';

export interface Metric {
  id: string;
  name: string;
  unit: string;
  connectionName: string;
  latest: { value: number; observedAt: number } | null;
}

interface Props {
  gauge: Gauge;
  metrics: Metric[];
  metricId: string | null;
  settings: AssignmentSettings;
  onChange: (metricId: string | null, settings: AssignmentSettings) => void;
  onSave: () => void;
  onClear: () => void;
  saving: boolean;
  savedAt: number | null;
  error: string | null;
  readOnly: boolean;
  /** Warning lights of this cluster (alerts and stale data can switch them on). */
  lights: { id: string; label: string }[];
}

export function defaultSettings(gauge: Gauge, metric?: Metric): AssignmentSettings {
  if (gauge.type === 'segment_display') return { kind: 'segment', decimals: 0, compact: true, suffix: '' };
  if (gauge.type === 'warning_light') return { kind: 'light', mode: 'rule', when: 'above', threshold: 0, blink: false, manual: 'off' };
  const v = metric?.latest?.value ?? 100;
  const max = niceMax(Math.max(Math.abs(v) * 1.6, 1));
  return { kind: 'needle', unit_label: metric?.unit ?? '', kpi_min: 0, kpi_max: max, multiplier: 1, damping_ms: 800 };
}

/** A round number above v, e.g. 173 → 200, 0.034 → 0.04. */
function niceMax(v: number) {
  const p = 10 ** Math.floor(Math.log10(v));
  return [1, 2, 2.5, 4, 5, 8, 10].map((m) => m * p).find((c) => c >= v) ?? v;
}

export function GaugeEditor({ gauge, metrics, metricId, settings, onChange, onSave, onClear, saving, savedAt, error, readOnly, lights }: Props) {
  const { t } = useTranslation();
  const metric = metrics.find((m) => m.id === metricId);
  const set = (patch: Partial<AssignmentSettings>) => onChange(metricId, { ...settings, ...patch } as AssignmentSettings);
  const num = (v: string) => (v === '' || Number.isNaN(Number(v)) ? 0 : Number(v));

  return (
    <form className="card stack editor" onSubmit={(e) => (e.preventDefault(), onSave())} aria-labelledby="editor-title">
      <div>
        <h2 id="editor-title">{t('editor.title', { gauge: gauge.label })}</h2>
        {gauge.type === 'needle' && <p className="faint">{t('editor.dialRange', { min: gauge.min, max: gauge.max, unit: gauge.unit })}</p>}
      </div>

      <fieldset disabled={readOnly} className="stack" style={{ border: 0, padding: 0 }}>
        {settings.kind === 'light' && (
          <div className="field">
            <label htmlFor="ed-lmode">{t('editor.lightMode')}</label>
            <select id="ed-lmode" value={settings.mode} onChange={(e) => onChange(e.target.value === 'manual' ? null : metricId, { ...settings, mode: e.target.value as LightSettings['mode'] })}>
              <option value="rule">{t('editor.lightModeRule')}</option>
              <option value="manual">{t('editor.lightModeManual')}</option>
            </select>
          </div>
        )}

        {!(settings.kind === 'light' && settings.mode === 'manual') && <div className="field">
          <label htmlFor="ed-metric">{t('editor.metric')}</label>
          <select
            id="ed-metric"
            value={metricId ?? ''}
            onChange={(e) => {
              const id = e.target.value || null;
              const m = metrics.find((x) => x.id === id);
              // Picking a KPI proposes a sensible scale from its current value.
              onChange(id, metricId === null && m ? defaultSettings(gauge, m) : settings);
            }}
          >
            <option value="">{t('editor.noMetric')}</option>
            {metrics.map((m) => (
              <option key={m.id} value={m.id}>{m.name} ({m.connectionName})</option>
            ))}
          </select>
          {metric?.latest && <p className="faint">{t('editor.current')}: {formatValue(metric.latest.value)} {metric.unit}</p>}
        </div>}

        {settings.kind === 'needle' && <NeedleFields s={settings} set={set} num={num} lights={lights} />}

        {settings.kind === 'segment' && (
          <div className="grid-2">
            <div className="field"><label htmlFor="ed-dec">Decimals</label><input id="ed-dec" type="number" min={0} max={4} value={settings.decimals} onChange={(e) => set({ decimals: num(e.target.value) })} /></div>
            <div className="field"><label htmlFor="ed-suf">Suffix</label><input id="ed-suf" maxLength={4} value={settings.suffix} onChange={(e) => set({ suffix: e.target.value })} /></div>
          </div>
        )}

        {settings.kind === 'light' && settings.mode === 'manual' && (
          <div className="field">
            <label htmlFor="ed-lman">{t('editor.lightState')}</label>
            <select id="ed-lman" value={settings.manual} onChange={(e) => set({ manual: e.target.value as LightSettings['manual'] })}>
              <option value="off">{t('editor.lightOff')}</option><option value="on">{t('editor.lightOn')}</option><option value="blink">{t('editor.lightBlink')}</option>
            </select>
            <p className="faint">{t('editor.lightManualHint')}</p>
          </div>
        )}

        {settings.kind === 'light' && settings.mode === 'rule' && (
          <>
            <div className="grid-2">
              <div className="field"><label htmlFor="ed-lw">{t('editor.lightWhen')}</label>
                <select id="ed-lw" value={settings.when} onChange={(e) => set({ when: e.target.value as 'above' | 'below', blink_at: undefined })}>
                  <option value="above">{t('editor.above')}</option><option value="below">{t('editor.below')}</option>
                </select></div>
              <div className="field"><label htmlFor="ed-lt">{t('editor.threshold')}</label><input id="ed-lt" type="number" step="any" value={settings.threshold} onChange={(e) => set({ threshold: num(e.target.value) })} /></div>
            </div>
            <label className="checkbox"><input type="checkbox" checked={settings.blink} onChange={(e) => set({ blink: e.target.checked })} /> {t('editor.lightBlinkAlways')}</label>
            {!settings.blink && (
              <div className="field">
                <label htmlFor="ed-lba">{t('editor.lightBlinkAt')}</label>
                <input id="ed-lba" type="number" step="any" value={settings.blink_at ?? ''} placeholder={t('editor.optional')} aria-describedby="ed-lba-hint" onChange={(e) => set({ blink_at: e.target.value === '' ? undefined : num(e.target.value) })} />
                <p className="faint" id="ed-lba-hint">{t('editor.lightBlinkAtHint', { direction: t(settings.when === 'above' ? 'editor.above' : 'editor.below') })}</p>
              </div>
            )}
          </>
        )}
      </fieldset>

      {error && <p className="error" role="alert">{error}</p>}
      {!readOnly && (
        <div className="row">
          <button className="btn" type="submit" disabled={saving}>{saving ? t('editor.saving') : t('editor.save')}</button>
          <button className="btn btn--ghost" type="button" onClick={onClear}>{t('editor.remove')}</button>
          {savedAt && !saving && <span className="ok" role="status">{t('editor.saved')}</span>}
        </div>
      )}
    </form>
  );
}

function NeedleFields({ s, set, num, lights }: { s: NeedleSettings; set: (p: Partial<NeedleSettings>) => void; num: (v: string) => number; lights: { id: string; label: string }[] }) {
  const { t } = useTranslation();
  return (
    <>
      <div className="field"><label htmlFor="ed-unit">{t('editor.unitLabel')}</label><input id="ed-unit" maxLength={24} value={s.unit_label} onChange={(e) => set({ unit_label: e.target.value })} /></div>
      <div className="grid-2">
        <div className="field"><label htmlFor="ed-min">{t('editor.kpiMin')}</label><input id="ed-min" type="number" step="any" value={s.kpi_min} onChange={(e) => set({ kpi_min: num(e.target.value) })} /></div>
        <div className="field"><label htmlFor="ed-max">{t('editor.kpiMax')}</label><input id="ed-max" type="number" step="any" value={s.kpi_max} onChange={(e) => set({ kpi_max: num(e.target.value) })} /></div>
      </div>
      <div className="grid-2">
        <div className="field">
          <label htmlFor="ed-mult">{t('editor.multiplier')}</label>
          <select id="ed-mult" value={s.multiplier} onChange={(e) => set({ multiplier: num(e.target.value) })} aria-describedby="ed-mult-hint">
            {[1, 10, 100, 1000, 10000, 100000, 1000000].map((m) => <option key={m} value={m}>{m === 1 ? '—' : `×${m.toLocaleString('en')}`}</option>)}
          </select>
        </div>
        <div className="field"><label htmlFor="ed-red">{t('editor.redline')}</label><input id="ed-red" type="number" step="any" value={s.redline_start ?? ''} onChange={(e) => set({ redline_start: e.target.value === '' ? undefined : num(e.target.value) })} /></div>
      </div>
      <p className="faint" id="ed-mult-hint">{t('editor.multiplierHint')}</p>

      <fieldset className="stack" style={{ border: 0, padding: 0 }}>
        <legend className="label" style={{ fontWeight: 600, marginBottom: '0.4rem' }}>{t('editor.alertTitle')}</legend>
        <div className="grid-2">
          <div className="field"><label htmlFor="ed-aw">{t('editor.alertWhen')}</label>
            <select id="ed-aw" value={s.alert?.when ?? ''} onChange={(e) => set({ alert: e.target.value ? { when: e.target.value as 'above' | 'below', threshold: s.alert?.threshold ?? s.kpi_max * 0.8, needle: s.alert?.needle ?? 'value', light: s.alert?.light, light_blink: s.alert?.light_blink ?? false } : undefined })}>
              <option value="">{t('editor.none')}</option><option value="above">{t('editor.above')}</option><option value="below">{t('editor.below')}</option>
            </select></div>
          {s.alert && <div className="field"><label htmlFor="ed-at">{t('editor.threshold')}</label><input id="ed-at" type="number" step="any" value={s.alert.threshold} onChange={(e) => set({ alert: { ...s.alert!, threshold: num(e.target.value) } })} /></div>}
        </div>
        {s.alert && (
          <label className="checkbox"><input type="checkbox" checked={s.alert.needle === 'redline'} onChange={(e) => set({ alert: { ...s.alert!, needle: e.target.checked ? 'redline' : 'value' } })} /> {t('editor.alertNeedle')}</label>
        )}
        {s.alert && lights.length > 0 && (
          <div className="grid-2">
            <div className="field"><label htmlFor="ed-al">{t('editor.alertLight')}</label>
              <select id="ed-al" value={s.alert.light ?? ''} onChange={(e) => set({ alert: { ...s.alert!, light: e.target.value || undefined } })}>
                <option value="">{t('editor.noLight')}</option>
                {lights.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
              </select></div>
            {s.alert.light && (
              <label className="checkbox" style={{ alignSelf: 'end' }}><input type="checkbox" checked={s.alert.light_blink} onChange={(e) => set({ alert: { ...s.alert!, light_blink: e.target.checked } })} /> {t('editor.lightBlink')}</label>
            )}
          </div>
        )}
      </fieldset>

      {lights.length > 0 && (
        <div className="field">
          <label htmlFor="ed-sl">{t('editor.staleLight')}</label>
          <select id="ed-sl" value={s.stale?.light ?? ''} onChange={(e) => set({ stale: e.target.value ? { light: e.target.value } : undefined })}>
            <option value="">{t('editor.noLight')}</option>
            {lights.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
          </select>
        </div>
      )}

      <fieldset className="stack" style={{ border: 0, padding: 0 }}>
        <legend className="label" style={{ fontWeight: 600, marginBottom: '0.4rem' }}>{t('editor.statusTitle')}</legend>
        <div className="field">
          <label htmlFor="ed-sd" className="visually-hidden">{t('editor.statusTitle')}</label>
          <select id="ed-sd" value={s.status?.direction ?? ''} onChange={(e) => {
            const d = e.target.value as '' | 'higher_is_better' | 'lower_is_better';
            if (!d) return set({ status: undefined });
            const span = s.kpi_max - s.kpi_min;
            set({ status: d === 'higher_is_better' ? { direction: d, amber_at: s.kpi_min + span * 0.4, red_at: s.kpi_min + span * 0.25 } : { direction: d, amber_at: s.kpi_min + span * 0.6, red_at: s.kpi_min + span * 0.75 } });
          }}>
            <option value="">{t('editor.statusNone')}</option><option value="higher_is_better">{t('editor.higherBetter')}</option><option value="lower_is_better">{t('editor.lowerBetter')}</option>
          </select>
        </div>
        {s.status && (
          <div className="grid-2">
            <div className="field"><label htmlFor="ed-amb">{t('editor.amberAt')}</label><input id="ed-amb" type="number" step="any" value={s.status.amber_at} onChange={(e) => set({ status: { ...s.status!, amber_at: num(e.target.value) } })} /></div>
            <div className="field"><label htmlFor="ed-redat">{t('editor.redAt')}</label><input id="ed-redat" type="number" step="any" value={s.status.red_at} onChange={(e) => set({ status: { ...s.status!, red_at: num(e.target.value) } })} /></div>
          </div>
        )}
      </fieldset>

      <div className="field">
        <label htmlFor="ed-damp">{t('editor.damping')}: {t('editor.dampingValue', { ms: s.damping_ms })}</label>
        <input id="ed-damp" type="range" min={0} max={4000} step={100} value={s.damping_ms} onChange={(e) => set({ damping_ms: num(e.target.value) })} />
      </div>
    </>
  );
}
